import {
  ORDER_INQUIRY_CONTENT_MAX,
  ORDER_INQUIRY_CONTENT_MIN,
  ORDER_INQUIRY_IMAGES_MAX,
  type OrderInquiryKind,
} from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { orderInquiryErrorMessage } from '@/entities/order-inquiry';
import { Button, cn, FormSection, ImageUploadPicker } from '@/shared/ui';
import type { OrderInquiryFormValues } from '../model/order-inquiry-form-values';
import { useOrderInquiryImageUploads } from '../model/use-order-inquiry-image-uploads';
import { OrderInquiryKindField } from './order-inquiry-kind-field';

interface OrderInquiryFormProps {
  readonly onSubmit: (values: OrderInquiryFormValues) => Promise<void>;
  readonly submitLabel: string;
}

export const OrderInquiryForm = ({
  onSubmit,
  submitLabel,
}: OrderInquiryFormProps): ReactElement => {
  const [kind, setKind] = useState<OrderInquiryKind | null>(null);
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const uploads = useOrderInquiryImageUploads();
  const length = Array.from(content.trim()).length;
  const valid =
    kind !== null && length >= ORDER_INQUIRY_CONTENT_MIN && length <= ORDER_INQUIRY_CONTENT_MAX;
  const rejectionDetail =
    uploads.rejections === null
      ? ''
      : [
          uploads.rejections.type > 0 && `형식 ${uploads.rejections.type}장`,
          uploads.rejections.size > 0 && `용량 ${uploads.rejections.size}장`,
          uploads.rejections.limit > 0 && `장수 ${uploads.rejections.limit}장`,
        ]
          .filter(Boolean)
          .join(', ');
  const blocked = uploads.isBusy || uploads.hasFailed;

  return (
    <form
      className="flex flex-col gap-12"
      onSubmit={async (event) => {
        event.preventDefault();
        if (kind === null || !valid || blocked || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
          await onSubmit({ kind, content: content.trim(), imageIds: uploads.readyIds });
        } catch (caught) {
          setError(orderInquiryErrorMessage(caught));
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <FormSection order={1} title="문의 종류">
        <OrderInquiryKindField value={kind} onChange={setKind} />
      </FormSection>
      <FormSection order={2} title="문의 내용">
        <div className="flex flex-col gap-2">
          <textarea
            aria-label="문의 내용"
            value={content}
            maxLength={ORDER_INQUIRY_CONTENT_MAX * 2}
            onChange={(event) => setContent(event.target.value)}
            rows={8}
            placeholder="어떤 문제가 있었는지 알려 주세요. 수량 부족이면 받은 수량, 불량이면 어디가 어떻게 잘못됐는지 적어 주시면 빨리 확인할 수 있어요."
            className="min-h-48 w-full resize-y rounded-2xl border border-input bg-background px-5 py-4 text-[15px] leading-relaxed transition-colors placeholder:text-muted-foreground focus-visible:border-foreground focus-visible:outline-none"
          />
          <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
            <span>{ORDER_INQUIRY_CONTENT_MIN}자 이상 써 주세요</span>
            <span
              className={cn(
                'tabular-nums',
                length > 0 && length < ORDER_INQUIRY_CONTENT_MIN && 'text-destructive',
              )}
            >
              {length.toLocaleString('ko-KR')} / {ORDER_INQUIRY_CONTENT_MAX.toLocaleString('ko-KR')}
            </span>
          </div>
        </div>
      </FormSection>
      <FormSection order={3} title="사진 (선택)">
        <div className="flex flex-col gap-3">
          <ImageUploadPicker
            items={uploads.items}
            max={ORDER_INQUIRY_IMAGES_MAX}
            onAdd={uploads.add}
            onRemove={uploads.remove}
            onRetry={uploads.retry}
          />
          {uploads.rejections !== null && (
            <p role="status" className="text-sm text-muted-foreground">
              JPG·PNG·WEBP, 장당 10MB 이하, 최대 {ORDER_INQUIRY_IMAGES_MAX}장까지 올릴 수 있어요.
              일부 사진은 추가하지 않았어요. ({rejectionDetail})
            </p>
          )}
          {uploads.hasFailed && (
            <p role="alert" className="text-sm text-destructive">
              올리지 못한 사진이 있어요. 다시 시도하거나 빼 주세요.
            </p>
          )}
        </div>
      </FormSection>
      {error !== null && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex flex-col gap-3 border-t border-foreground/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          접수하면 운영자가 확인한 뒤 답변을 메일로 보내 드려요.
        </p>
        <Button
          type="submit"
          className="h-12 w-full px-10 text-[15px] sm:w-auto"
          disabled={!valid || blocked || submitting}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
